import { safeSetItem } from '@/lib/safeStorage';
/**
 * Aphenis-branded audio tones synthesized via Web Audio API.
 * Designed to reflect Aphenis's cosmic, futuristic, privacy-focused identity.
 *
 * Tones:
 * - Outgoing ringtone: Deep-space pulse with harmonic shimmer & sub-bass undertow
 * - Incoming ringtone: Crystalline ascending cascade with holographic resonance
 * - Notification: Quick ethereal "data-burst" chime with metallic decay
 */

import { getSharedAudioContext, resetSharedAudioContext } from './audioContextPool';

let ctx: AudioContext | null = null;
let resumePromise: Promise<void> | null = null;

function getCtx(): AudioContext {
  ctx = getSharedAudioContext();
  if (ctx.state === 'suspended') {
    resumePromise = ctx.resume();
  }
  return ctx;
}

/** Ensure AudioContext is ready before scheduling nodes */
async function ensureCtxReady(): Promise<AudioContext> {
  const ac = getCtx();
  if (resumePromise) {
    await resumePromise;
    resumePromise = null;
  }
  // Double-check: on Android WebView, resume() can resolve while still suspended
  if (ac.state === 'suspended') {
    await ac.resume();
  }
  return ac;
}

// ── Volume control ──
const TONE_VOLUME_KEY = 'aphenis_tone_volume';

export function getToneVolume(): number {
  try {
    const v = localStorage.getItem(TONE_VOLUME_KEY);
    return v !== null ? parseFloat(v) : 0.35;
  } catch { return 0.35; }
}

export function setToneVolume(v: number): void {
  safeSetItem(TONE_VOLUME_KEY, String(Math.max(0, Math.min(1, v))));
}

// ── Mute control ──
const TONE_MUTED_KEY = 'aphenis_tones_muted';

export function isTonesMuted(): boolean {
  try { return localStorage.getItem(TONE_MUTED_KEY) === 'true'; } catch { return false; }
}

export function setTonesMuted(v: boolean): void {
  safeSetItem(TONE_MUTED_KEY, v ? 'true' : 'false');
}

// ── Helpers ──

/** Creates a convolver for subtle space-like reverb */
function createSpaceReverb(ac: AudioContext, duration = 1.5, decay = 2.5): ConvolverNode {
  const rate = ac.sampleRate;
  const len = rate * duration;
  const impulse = ac.createBuffer(2, len, rate);
  for (let ch = 0; ch < 2; ch++) {
    const data = impulse.getChannelData(ch);
    for (let i = 0; i < len; i++) {
      data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, decay);
    }
  }
  const conv = ac.createConvolver();
  conv.buffer = impulse;
  return conv;
}

/** Creates a bandpass-filtered noise burst for texture */
function createNoiseGrain(ac: AudioContext, t: number, dur: number, freq: number, gain: GainNode) {
  const bufLen = ac.sampleRate * dur;
  const buf = ac.createBuffer(1, bufLen, ac.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < bufLen; i++) data[i] = Math.random() * 2 - 1;
  const src = ac.createBufferSource();
  src.buffer = buf;

  const bp = ac.createBiquadFilter();
  bp.type = 'bandpass';
  bp.frequency.setValueAtTime(freq, t);
  bp.Q.setValueAtTime(12, t);

  src.connect(bp);
  bp.connect(gain);
  src.start(t);
  src.stop(t + dur);
}

// ══════════════════════════════════════════════════════════════
// OUTGOING RINGTONE — Deep-space pulse
// A mysterious, pulsing dual-tone with sub-bass undertow and
// shimmering harmonics — like a signal reaching across the void.
// Pattern: ~1.4s pulse, ~1.6s silence, repeat
// ══════════════════════════════════════════════════════════════

let outgoingInterval: ReturnType<typeof setInterval> | null = null;

function playOutgoingBurst(): void {
  if (isTonesMuted()) return;
  const ac = getCtx();
  // If context is still suspended, skip this burst (next interval will retry)
  if (ac.state === 'suspended') { ac.resume(); return; }
  const now = ac.currentTime;
  const vol = getToneVolume();

  // Master gain → reverb → destination
  const master = ac.createGain();
  master.gain.setValueAtTime(0, now);
  const reverb = createSpaceReverb(ac, 1.2, 3);
  const dry = ac.createGain();
  dry.gain.setValueAtTime(0.75, now);
  const wet = ac.createGain();
  wet.gain.setValueAtTime(0.25, now);
  master.connect(dry);
  master.connect(reverb);
  reverb.connect(wet);
  dry.connect(ac.destination);
  wet.connect(ac.destination);

  // Pulse 1: warm fade in-out
  master.gain.linearRampToValueAtTime(vol * 0.22, now + 0.12);
  master.gain.setValueAtTime(vol * 0.22, now + 0.45);
  master.gain.exponentialRampToValueAtTime(0.001, now + 0.65);
  // Pulse 2
  master.gain.setValueAtTime(0, now + 0.75);
  master.gain.linearRampToValueAtTime(vol * 0.25, now + 0.87);
  master.gain.setValueAtTime(vol * 0.25, now + 1.15);
  master.gain.exponentialRampToValueAtTime(0.001, now + 1.4);

  // Primary tone: A4 (440) with slight detune for thickness
  const osc1 = ac.createOscillator();
  osc1.type = 'sine';
  osc1.frequency.setValueAtTime(440, now);
  osc1.detune.setValueAtTime(-6, now);
  osc1.connect(master);
  osc1.start(now);
  osc1.stop(now + 1.5);

  // Harmonic: Perfect fifth up (E5 ~659) — aphenis signature interval
  const osc2 = ac.createOscillator();
  osc2.type = 'sine';
  osc2.frequency.setValueAtTime(659.25, now);
  osc2.detune.setValueAtTime(4, now);
  const g2 = ac.createGain();
  g2.gain.setValueAtTime(0.6, now);
  osc2.connect(g2);
  g2.connect(master);
  osc2.start(now);
  osc2.stop(now + 1.5);

  // Sub-bass undertow: low sine sweep (55 → 44 Hz)
  const sub = ac.createOscillator();
  sub.type = 'sine';
  sub.frequency.setValueAtTime(55, now);
  sub.frequency.exponentialRampToValueAtTime(44, now + 1.4);
  const subGain = ac.createGain();
  subGain.gain.setValueAtTime(vol * 0.12, now);
  subGain.gain.exponentialRampToValueAtTime(0.001, now + 1.4);
  sub.connect(subGain);
  subGain.connect(ac.destination);
  sub.start(now);
  sub.stop(now + 1.5);

  // Shimmer overtone: high triangle with LFO tremolo
  const shimmer = ac.createOscillator();
  shimmer.type = 'triangle';
  shimmer.frequency.setValueAtTime(1318.5, now); // E6
  const shimGain = ac.createGain();
  shimGain.gain.setValueAtTime(vol * 0.04, now);
  shimGain.gain.exponentialRampToValueAtTime(0.001, now + 1.3);
  shimmer.connect(shimGain);
  shimGain.connect(master);
  shimmer.start(now);
  shimmer.stop(now + 1.5);

  // Noise texture grain at pulse peaks
  const noiseG = ac.createGain();
  noiseG.gain.setValueAtTime(vol * 0.015, now);
  noiseG.gain.exponentialRampToValueAtTime(0.001, now + 1.4);
  noiseG.connect(reverb);
  createNoiseGrain(ac, now + 0.05, 0.3, 3000, noiseG);
  createNoiseGrain(ac, now + 0.78, 0.3, 3500, noiseG);
}

export function startOutgoingRingtone(): void {
  stopOutgoingRingtone();
  // Delegate to user's selected ringtone instead of hardcoded synth
  try {
    import('./callRingtones').then(({ startSelectedRingtone }) => {
      startSelectedRingtone();
    }).catch(() => {
      // Fallback to hardcoded burst
      playOutgoingBurst();
      outgoingInterval = setInterval(playOutgoingBurst, 3000);
    });
  } catch {
    playOutgoingBurst();
    outgoingInterval = setInterval(playOutgoingBurst, 3000);
  }
}

export function stopOutgoingRingtone(): void {
  if (outgoingInterval) { clearInterval(outgoingInterval); outgoingInterval = null; }
  // Also stop the selected ringtone if it was delegated
  try {
    import('./callRingtones').then(({ stopSelectedRingtone }) => {
      stopSelectedRingtone();
    }).catch(() => {});
  } catch {}
}

// ══════════════════════════════════════════════════════════════
// INCOMING RINGTONE — Crystalline cascade
// A rising 5-note holographic arpeggio with resonant filter sweep,
// shimmering overtones, and a ghostly reverb tail.
// Pattern: ~1.2s cascade, ~1.3s silence, repeat
// ══════════════════════════════════════════════════════════════

let incomingInterval: ReturnType<typeof setInterval> | null = null;

function playIncomingArpeggio(): void {
  if (isTonesMuted()) return;
  const ac = getCtx();
  if (ac.state === 'suspended') { ac.resume(); return; }
  const now = ac.currentTime;
  const vol = getToneVolume();

  // Reverb bus
  const reverb = createSpaceReverb(ac, 1.8, 2.2);
  const wet = ac.createGain();
  wet.gain.setValueAtTime(0.3, now);
  reverb.connect(wet);
  wet.connect(ac.destination);

  // Notes: B4 → D#5 → F#5 → B5 → D#6 (B major — bright, ascending, ethereal)
  const notes = [493.88, 622.25, 739.99, 987.77, 1244.51];
  const noteLen = 0.14;
  const gap = 0.04;

  notes.forEach((freq, i) => {
    const t = now + i * (noteLen + gap);

    // Main tone — sine for purity
    const gain = ac.createGain();
    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(vol * 0.28, t + 0.02);
    gain.gain.setValueAtTime(vol * 0.28, t + noteLen * 0.5);
    gain.gain.exponentialRampToValueAtTime(0.001, t + noteLen + 0.18);
    gain.connect(ac.destination);
    gain.connect(reverb);

    const osc = ac.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(freq, t);
    // Slight pitch bend up for "holographic" feel
    osc.frequency.linearRampToValueAtTime(freq * 1.003, t + noteLen);
    osc.connect(gain);
    osc.start(t);
    osc.stop(t + noteLen + 0.22);

    // Crystal overtone — filtered triangle 2 octaves up
    const crystal = ac.createOscillator();
    crystal.type = 'triangle';
    crystal.frequency.setValueAtTime(freq * 4, t);
    const cGain = ac.createGain();
    cGain.gain.setValueAtTime(vol * 0.05, t);
    cGain.gain.exponentialRampToValueAtTime(0.001, t + noteLen + 0.12);

    // Resonant bandpass for "glass" quality
    const bp = ac.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.setValueAtTime(freq * 4, t);
    bp.Q.setValueAtTime(8, t);

    crystal.connect(bp);
    bp.connect(cGain);
    cGain.connect(ac.destination);
    cGain.connect(reverb);
    crystal.start(t);
    crystal.stop(t + noteLen + 0.15);

    // Sub-harmonic ghost (one octave below, very quiet)
    if (i < 3) {
      const ghost = ac.createOscillator();
      ghost.type = 'sine';
      ghost.frequency.setValueAtTime(freq / 2, t);
      const gGain = ac.createGain();
      gGain.gain.setValueAtTime(vol * 0.06, t);
      gGain.gain.exponentialRampToValueAtTime(0.001, t + noteLen + 0.1);
      ghost.connect(gGain);
      gGain.connect(reverb);
      ghost.start(t);
      ghost.stop(t + noteLen + 0.12);
    }
  });

  // Trailing noise shimmer after the last note
  const trailT = now + notes.length * (noteLen + gap);
  const trailG = ac.createGain();
  trailG.gain.setValueAtTime(vol * 0.02, trailT);
  trailG.gain.exponentialRampToValueAtTime(0.001, trailT + 0.4);
  trailG.connect(reverb);
  createNoiseGrain(ac, trailT, 0.3, 5000, trailG);
}

export function startIncomingRingtone(): void {
  stopIncomingRingtone();
  playIncomingArpeggio();
  incomingInterval = setInterval(playIncomingArpeggio, 2500);
}

export function stopIncomingRingtone(): void {
  if (incomingInterval) { clearInterval(incomingInterval); incomingInterval = null; }
}

// ══════════════════════════════════════════════════════════════
// NOTIFICATION SOUND — Holographic data-burst
// A rapid two-tone "blip" with metallic resonance, filtered noise
// texture, and a brief reverb tail — like encrypted data arriving.
// ══════════════════════════════════════════════════════════════

export async function playNotificationSound(): Promise<void> {
  // Delegate to selected tone
  try {
    const { playSelectedNotificationTone } = await import('./notificationTones');
    await playSelectedNotificationTone();
    return;
  } catch { /* fallback below */ }
  const ac = await ensureCtxReady();
  const now = ac.currentTime;
  const vol = getToneVolume();

  // Mini reverb
  const reverb = createSpaceReverb(ac, 0.6, 4);
  const wet = ac.createGain();
  wet.gain.setValueAtTime(0.2, now);
  reverb.connect(wet);
  wet.connect(ac.destination);

  // Note 1: B5 — sharp attack, quick decay
  const g1 = ac.createGain();
  g1.gain.setValueAtTime(0, now);
  g1.gain.linearRampToValueAtTime(vol * 0.3, now + 0.01);
  g1.gain.exponentialRampToValueAtTime(0.001, now + 0.2);
  g1.connect(ac.destination);
  g1.connect(reverb);

  const o1 = ac.createOscillator();
  o1.type = 'sine';
  o1.frequency.setValueAtTime(987.77, now); // B5
  o1.frequency.linearRampToValueAtTime(993, now + 0.15); // micro bend
  o1.connect(g1);
  o1.start(now);
  o1.stop(now + 0.25);

  // Note 2: D#6 — major third up, slightly delayed
  const g2 = ac.createGain();
  g2.gain.setValueAtTime(0, now + 0.07);
  g2.gain.linearRampToValueAtTime(vol * 0.25, now + 0.08);
  g2.gain.exponentialRampToValueAtTime(0.001, now + 0.32);
  g2.connect(ac.destination);
  g2.connect(reverb);

  const o2 = ac.createOscillator();
  o2.type = 'sine';
  o2.frequency.setValueAtTime(1244.51, now + 0.07); // D#6
  o2.connect(g2);
  o2.start(now + 0.07);
  o2.stop(now + 0.35);

  // Metallic resonance — high filtered ping
  const metal = ac.createOscillator();
  metal.type = 'square';
  metal.frequency.setValueAtTime(3951, now); // B7
  const metalBP = ac.createBiquadFilter();
  metalBP.type = 'bandpass';
  metalBP.frequency.setValueAtTime(3951, now);
  metalBP.Q.setValueAtTime(30, now);
  const metalG = ac.createGain();
  metalG.gain.setValueAtTime(vol * 0.02, now);
  metalG.gain.exponentialRampToValueAtTime(0.001, now + 0.15);
  metal.connect(metalBP);
  metalBP.connect(metalG);
  metalG.connect(ac.destination);
  metalG.connect(reverb);
  metal.start(now);
  metal.stop(now + 0.2);

  // Noise texture burst — "data arrival" feel
  const noiseG = ac.createGain();
  noiseG.gain.setValueAtTime(vol * 0.025, now);
  noiseG.gain.exponentialRampToValueAtTime(0.001, now + 0.12);
  noiseG.connect(reverb);
  createNoiseGrain(ac, now, 0.1, 6000, noiseG);
}
